import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaGlobe, FaCogs, FaUsers, FaPhone, FaSatelliteDish, FaHeadset, FaRocket, FaShieldAlt, FaChartLine } from 'react-icons/fa';
import './About.css';

const About = () => {
  const services = [
    {
      icon: <FaGlobe />,
      title: "A–Z VoIP Termination with Unified Communication",
      description: "Experience seamless global connectivity through our world-class VoIP termination network. Spanning every continent, our advanced platform delivers unified communication solutions that transform business interactions with unprecedented reliability and crystal-clear quality."
    },
    {
      icon: <FaPhone />,
      title: "SIP Origination",
      description: "Supercharge your inbound communications with our enterprise-grade SIP origination services. Engineered for maximum stability and scalability, we deliver superior call quality across all major international markets, ensuring your business stays connected anywhere in the world."
    },
    {
      icon: <FaSatelliteDish />,
      title: "Virtual DID Numbers",
      description: "Establish a powerful global presence with our premium virtual DID numbers. Strategically designed for your business needs, these numbers enable you to create local identities in key markets while managing all communications through our sophisticated unified platform."
    }
  ];

  const features = [
    {
      icon: <FaRocket />,
      title: "Innovation & Efficiency",
      description: "We relentlessly pursue technological excellence through continuous innovation and cutting-edge solutions. Our commitment to advancement delivers intelligent, lightning-fast, and supremely efficient communication systems that evolve with your business objectives."
    },
    {
      icon: <FaGlobe />,
      title: "Global Coverage",
      description: "Leveraging strategic partnerships with premier international telecom providers, our meticulously distributed infrastructure guarantees exceptional call quality and unwavering connectivity across every corner of the globe."
    },
    {
      icon: <FaUsers />,
      title: "Experienced Teams",
      description: "Our elite team of VoIP specialists brings decades of industry expertise, seamlessly blending advanced technical mastery with strategic business acumen to deliver customized, future-ready solutions that drive your success."
    }
  ];

  return (
    <div className="about-container">
      {/* Hero Section */}
      <section className="about-hero">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="about-hero-title">Discover the Excellence of Direct Route Networks' Communication Solutions</h1>
            <p className="about-hero-subtitle">
              Founded in 2024 by Mr. Umair Malik, Direct Route Networks was built on a bold vision — to revolutionize business communication through cutting-edge, secure, and scalable telecom solutions that empower organizations worldwide.
            </p>
            <p className="about-hero-description">
              With extensive expertise in technology and visionary leadership, Mr. Malik established Direct Route Networks to address the critical need for reliable, enterprise-grade communication infrastructure that drives business success.
            </p>
            <p className="about-hero-conclusion">
              Today, Direct Route Networks stands as the premier choice for forward-thinking enterprises seeking innovative voice and data services, meticulously engineered to maximize efficiency, strengthen connectivity, and accelerate business growth.
            </p>
            <Link to="/contact" className="about-cta-btn">Get Started</Link>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="about-services">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="about-section-title">Discover the Versatility of Our Communication Solutions</h2>
            <div className="about-services-grid">
              {services.map((service, index) => (
                <motion.div
                  key={index}
                  className="about-service-card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  viewport={{ once: true }}
                >
                  <div className="service-icon">{service.icon}</div>
                  <h3 className="service-title">{service.title}</h3>
                  <p className="service-description">{service.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="about-features">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="about-section-title">Empowering Your Connectivity</h2>
            <div className="about-features-grid">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  className="about-feature-card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  viewport={{ once: true }}
                >
                  <div className="feature-icon">{feature.icon}</div>
                  <h3 className="feature-title">{feature.title}</h3>
                  <p className="feature-description">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="about-cta">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="cta-title">Ready to Transform Your Communication?</h2>
            <p className="cta-description">
              Join thousands of businesses that trust Direct Route Networks for their communication needs.
            </p>
            <Link to="/contact" className="cta-btn">Get Started Today</Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;